'use client';

import { StatusMessage } from '@/shared/ui/core/status-message';
import { useState, useTransition } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Text } from '@/shared/ui/core';
import { ClearableInput } from '@/shared/ui/kit/clearable-input';
import { PasswordInput } from '@/shared/ui/kit/password-input';
import { loginSchema, LoginFormData } from '@/shared/lib/validation/login';
import { VERIFICATION_PATHNAME } from '@/shared/constants/pathnames';
import { LoginFormProps } from '../model/login-form.props';
import { loginRequest, LoginError } from '../api/login';
import styles from './login-form.module.scss';


export const LoginForm = ({ email, ForgotPassword, redirectTo = VERIFICATION_PATHNAME }: LoginFormProps) => {
    const [ isPending, startTransition ]  = useTransition();
    const [ submitError, setSubmitError ] = useState<string | null>(null);

    const { register, control, handleSubmit, setValue, clearErrors, formState: { errors } } = useForm<LoginFormData>({
        resolver      : zodResolver(loginSchema),
        mode          : 'onBlur',
        reValidateMode: 'onChange',
        defaultValues : { email: email ?? '', password: '' },
    });

    const [ emailVal, passwordVal ] = useWatch({ control, name: [ 'email', 'password' ] });

    const onSubmit = handleSubmit((data) => {
        setSubmitError(null);

        startTransition(async () => {
            try {
                await loginRequest(data);
                window.location.href = redirectTo;
            } catch (error) {
                if (error instanceof LoginError && error.status === 401) {
                    setSubmitError('credentials');
                } else if (error instanceof Error) {
                    setSubmitError(error.message);
                } else {
                    setSubmitError('Something went wrong');
                }
            }
        });
    });

    return (
        <>
            {
                submitError && submitError === 'credentials' &&
                <StatusMessage
                    status='destructive'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='destructive'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            Credentials are incorrect
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='destructive'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        Check your login and password and try again
                    </Text>
                </StatusMessage>
            }

            {
                submitError && submitError !== 'credentials' &&
                <StatusMessage
                    status='destructive'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='destructive'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            Error occurred
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='destructive'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        { submitError }
                    </Text>
                </StatusMessage>
            }

            <div data-template="login-form">
                <ClearableInput
                    label="E-mail:"
                    placeholder="mail@example.com"
                    autoComplete="email"
                    type="email"
                    error={ errors.email?.message }
                    value={ emailVal }
                    onClear={ () => setValue('email', '', { shouldValidate: true }) }
                    { ...register('email') }
                    onFocus={ () => {
                        clearErrors('email');
                        setSubmitError(null);
                    } }
                />

                <PasswordInput
                    className={ styles.password }
                    error={ errors.password?.message }
                    value={ passwordVal }
                    onClear={ () => setValue('password', '', { shouldValidate: true }) }
                    { ...register('password') }
                    onFocus={ () => {
                        clearErrors('password');
                        setSubmitError(null);
                    } }
                />

                { ForgotPassword }

                <Button
                    className={ styles.button }
                    fullWidth
                    loading={ isPending }
                    onClick={ onSubmit }
                >
                    { isPending ? 'Logging in…' : 'Log in' }
                </Button>
            </div>
        </>
    );
};
